import React from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';

const ITEM_TYPES = {
    SHELL: 'shell',
    ROOM: 'room',
    FURNITURE: 'furniture'
};

const HEIGHT_MAP = {
    fusion_wall_thickness: 10,
    room_wall_height: 120, // 벽 높이
    shell_floor: 2,
    room_floor: 2,
    furniture_default: 50,
    subType_bed: 40,
    subType_sofa: 60,
    subType_table: 70,
    subType_closet: 160,
    subType_door: 180,
    subType_sliding_door: 180,
    subType_window: 100,
    subType_chair: 45,
    subType_plant: 90,
    subType_entrance: 2 // 현관은 바닥에 깔리도록
};

const Shell3D = ({ item, posX, posZ }) => {
    // 쉘(외곽)은 가장 밑바닥에 깔리는 기준 평면
    return (
        <mesh position={[posX, HEIGHT_MAP.shell_floor / 2, posZ]} rotation={[0, -(item.rotation || 0) * (Math.PI / 180), 0]} receiveShadow>
            <boxGeometry args={[item.width, HEIGHT_MAP.shell_floor, item.height]} />
            <meshStandardMaterial color="#b2bec3" />
        </mesh>
    );
};

const Room3D = ({ item, posX, posZ }) => {
    const W = item.width;
    const H = item.height;
    const T = HEIGHT_MAP.fusion_wall_thickness;
    const WH = HEIGHT_MAP.room_wall_height;

    const rotationY = -(item.rotation || 0) * (Math.PI / 180);

    // 현관 등 벽이 필요 없는 방도 고려
    const isEntrance = item.subType === 'entrance';
    const trueWH = isEntrance ? 0 : WH;

    return (
        <group position={[posX, HEIGHT_MAP.shell_floor, posZ]} rotation={[0, rotationY, 0]}>
            {/* 방 바닥 */}
            <mesh position={[0, HEIGHT_MAP.room_floor / 2, 0]} castShadow receiveShadow>
                <boxGeometry args={[W, HEIGHT_MAP.room_floor, H]} />
                <meshStandardMaterial color={item.color} opacity={item.opacity || 1} transparent={item.opacity < 1} />
            </mesh>

            {/* 벽체 (현관이 아닐 때만) */}
            {!isEntrance && (
                <>
                    {/* Top wall */}
                    <mesh position={[0, trueWH / 2, -H / 2 + T / 2]} castShadow receiveShadow>
                        <boxGeometry args={[W, trueWH, T]} />
                        <meshStandardMaterial color="#f5f6fa" />
                    </mesh>
                    {/* Bottom wall */}
                    <mesh position={[0, trueWH / 2, H / 2 - T / 2]} castShadow receiveShadow>
                        <boxGeometry args={[W, trueWH, T]} />
                        <meshStandardMaterial color="#f5f6fa" />
                    </mesh>
                    {/* Left wall */}
                    <mesh position={[-W / 2 + T / 2, trueWH / 2, 0]} castShadow receiveShadow>
                        <boxGeometry args={[T, trueWH, H - T * 2]} />
                        <meshStandardMaterial color="#f5f6fa" />
                    </mesh>
                    {/* Right wall */}
                    <mesh position={[W / 2 - T / 2, trueWH / 2, 0]} castShadow receiveShadow>
                        <boxGeometry args={[T, trueWH, H - T * 2]} />
                        <meshStandardMaterial color="#f5f6fa" />
                    </mesh>
                </>
            )}
        </group>
    );
};

const Furniture3D = ({ item, posX, posZ }) => {
    let W = item.width;
    let H = item.height;

    let height = HEIGHT_MAP.furniture_default;
    let elevation = HEIGHT_MAP.shell_floor + HEIGHT_MAP.room_floor;
    let isGlass = false;

    if (item.subType === 'bed') height = HEIGHT_MAP.subType_bed;
    else if (item.subType === 'sofa') height = HEIGHT_MAP.subType_sofa;
    else if (item.subType === 'table') height = HEIGHT_MAP.subType_table;
    else if (item.subType === 'closet') height = HEIGHT_MAP.subType_closet;
    else if (item.subType === 'chair') height = HEIGHT_MAP.subType_chair;
    else if (item.subType === 'plant') height = HEIGHT_MAP.subType_plant;
    else if (item.subType === 'door') {
        height = HEIGHT_MAP.subType_door;
        W = 10; // 문은 얇게 치환
        isGlass = true;
    }
    else if (item.subType === 'sliding-door') {
        height = HEIGHT_MAP.subType_sliding_door;
        H = 10; // 미닫이문 두께
        isGlass = true;
    }
    else if (item.subType === 'window') {
        height = HEIGHT_MAP.subType_window;
        H = 10; // 창문 두께
        elevation += 50; // 창문은 허리춤 위로 띄우기
        isGlass = true;
    }

    const posY = elevation + height / 2;
    const rotationY = -(item.rotation || 0) * (Math.PI / 180);

    return (
        <group position={[posX, posY, posZ]} rotation={[0, rotationY, 0]}>
            <mesh castShadow receiveShadow>
                <boxGeometry args={[W, height, H]} />
                <meshStandardMaterial color={item.color} transparent={isGlass || item.opacity < 1} opacity={isGlass ? 0.3 : (item.opacity || 1)} />
            </mesh>
        </group>
    );
};

const getGlobalCenter = (item) => {
    const rad = (item.rotation || 0) * (Math.PI / 180);
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const cx = item.x + (item.width / 2) * cos - (item.height / 2) * sin;
    const cy = item.y + (item.width / 2) * sin + (item.height / 2) * cos;
    return { cx, cy };
};

const WaGgu3DViewer = ({ items, shellConfig }) => {
    // 씬의 중심 잡기 (외곽 기준점)
    const shellItem = items.find(i => i.type === ITEM_TYPES.SHELL);

    let centerX = (shellConfig?.width || 1000) / 2;
    let centerZ = (shellConfig?.height || 800) / 2;
    if (shellItem) {
        const c = getGlobalCenter(shellItem);
        centerX = c.cx;
        centerZ = c.cy;
    }

    const floorWidth = shellItem ? shellItem.width + 1000 : (shellConfig?.width || 1000) + 1000;
    const floorHeight = shellItem ? shellItem.height + 1000 : (shellConfig?.height || 800) + 1000;

    return (
        <div style={{ width: '100%', height: '100%', backgroundColor: '#f1f2f6' }}>
            <Canvas shadows gl={{ antialias: true, logarithmicDepthBuffer: true }}>
                <PerspectiveCamera makeDefault position={[0, Math.max(floorWidth, floorHeight) * 0.7, Math.max(floorWidth, floorHeight) * 0.5]} fov={50} />
                <OrbitControls target={[0, 0, 0]} maxPolarAngle={Math.PI / 2 - 0.05} />

                {/* 조명 설정 */}
                <ambientLight intensity={0.5} />
                <directionalLight
                    position={[300, 1000, 500]}
                    intensity={1.2}
                    castShadow
                    shadow-bias={-0.001}
                    shadow-mapSize={[2048, 2048]}
                    shadow-camera-far={3000}
                    shadow-camera-left={-2000}
                    shadow-camera-right={2000}
                    shadow-camera-top={2000}
                    shadow-camera-bottom={-2000}
                />
                <pointLight position={[-500, 500, -500]} intensity={0.5} />

                {/* 가장 밑바닥 환경 (마루바닥 느낌) */}
                <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]} receiveShadow>
                    <planeGeometry args={[floorWidth * 2, floorHeight * 2]} />
                    <meshStandardMaterial color="#eac2a4" roughness={0.8} />
                </mesh>

                {/* 도면 요소 렌더링 */}
                {items.map(item => {
                    const c = getGlobalCenter(item);
                    const posX = c.cx - centerX;
                    const posZ = c.cy - centerZ;

                    if (item.type === ITEM_TYPES.SHELL) return <Shell3D key={item.id} item={item} posX={posX} posZ={posZ} />;
                    if (item.type === ITEM_TYPES.ROOM) return <Room3D key={item.id} item={item} posX={posX} posZ={posZ} />;
                    if (item.type === ITEM_TYPES.FURNITURE) return <Furniture3D key={item.id} item={item} posX={posX} posZ={posZ} />;
                    return null;
                })}
            </Canvas>
        </div>
    );
};

export default WaGgu3DViewer;
